import { useEffect, useMemo, useRef, useState } from "react";
import './Quiz.css';
import { FormStateProps } from "../../app/models/formStateProps";
import { ModalConfig } from "../../app/models/modalConfig";
import { QuizProps } from "../../app/models/quizProps";
import { Player } from "../../app/models/player";
import Swal from "sweetalert2";
import {v4 as uuid} from "uuid";
import agent from "../../app/api/agent";
import UserDataModal, { UserDataValues } from "../UserDataModal";

type Step = "game" | "scoreboard";

interface QuizGameProps {
  isActive?: boolean;
  collectUserData: number;
  quiz: QuizProps['quiz'];
  questions?: QuizProps['questions'];
  formState: FormStateProps['formState'];
  modalConfig: ModalConfig;
  campaignId: string;
  campaignTitle: string;
  textColor: string;
}

const formatMMSS = (totalSeconds: number) => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const mm = Math.floor(s / 60)
    .toString()
    .padStart(2, "0");
  const ss = (s % 60).toString().padStart(2, "0");
  return `${mm}:${ss}`;
};

function rand<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

type Ans = { id: string; text: string; isCorrect: boolean; questionId: string };
type Q = { id: string; text: string; answers?: Ans[]; difficulty?: number; quizId: string };

function shuffle<T>(arr: T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function pickN<T>(arr: T[], n: number): T[] {
  if (n <= 0) return [];
  if (n >= arr.length) return arr.slice();
  return shuffle(arr).slice(0, n);
}

const Quiz: React.FC<QuizGameProps> = ({
  isActive,
  collectUserData,
  quiz,
  questions = [],
  formState,
  modalConfig,
  campaignId,
  campaignTitle,
  textColor
}) => {
  // moves/time tracking
  const [moves, setMoves] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<number | null>(null);
  const isTimeMode = (quiz?.quizType ?? 1) === 1;

  // flow
  const [step, setStep] = useState<Step>("game");
  const [qIndex, setQIndex] = useState(0);
  const [locked, setLocked] = useState(false);
  const [answerJustClicked, setAnswerJustClicked] = useState<number | null>(null);
  const [correctCount, setCorrectCount] = useState(0);

  // scoreboard + after-create id
  const [currentGameId, setCurrentGameId] = useState<string | null>(null);
  const [scoreboard, setScoreboard] = useState<{
    total: number;
    rank: number;
    entries: { rank: number; name: string; points: number; isActive: boolean }[];
  } | null>(null);

  // form handling (before/after)
  const [openModal, setOpenModal] = useState(false);
  const [player, setPlayer] = useState<Player | null>(null);
  const [dataCollected, setDataCollected] = useState(false);

  const initialFormValues: any = {
    firstName: "",
    lastName: "",
    email: formState.formEmail ? "" : undefined,
    phone: formState.formPhone ? "" : undefined,
    company: formState.formCompany ? "" : undefined,
    gender: formState.formGender ? 1 : undefined,
    formAddPermissionChecked: false,
    formAddPermission2Checked: false,
    ...(formState.bonusField && { [formState.bonusField]: "" }),
  };

  // pool count for Swals
  const totalAvailable = Array.isArray(questions)
    ? questions.filter((q) => q && q.text && q.answers && q.answers.length >= 2).length
    : 0;

  // warn only if desired > pool
  useEffect(() => {
    if (!totalAvailable) return;
    const qa = Number(quiz?.questionAmount);

    if (Number.isFinite(qa) && qa > totalAvailable) {
      Swal.fire({
        icon: "warning",
        title: "Soru Sayısı Aşıldı",
        text: `İstenen soru sayısı (${qa}), mevcut soru adedinden (${totalAvailable}) büyük.`,
        confirmButtonText: "Tamam",
        allowOutsideClick: true,
        allowEscapeKey: true,
      });
    }
  }, [totalAvailable, quiz?.questionAmount]);

  // CATEGORY-AWARE preparation
  const preparedQuestions = useMemo(() => {
    const pool: Q[] = Array.isArray(questions)
      ? (questions.filter((q) => q && q.text && q.answers && q.answers.length >= 2) as Q[])
      : [];
    const total = pool.length;

    // desired count (default 5 if invalid/missing)
    let desired = Number(quiz?.questionAmount);
    if (!Number.isFinite(desired) || desired <= 0) desired = 5;

    const count = Math.min(desired, total);

    const hasCategory = Boolean(quiz?.hasCategory);

    if (!hasCategory) {
      // classic: sample-shuffle from all, shuffle answers per question
      return pickN(pool, count).map((q) => ({
        ...q,
        answers: shuffle(q.answers || []),
      }));
    }

    // hasCategory = true ⇒ cycle categories 1,2,3,... then loop; randomize within each category
    // 1) group by difficulty (undefined goes to bucket "0" to avoid crash)
    const byCat = new Map<number, Q[]>();
    for (const q of pool) {
      const cat = Number.isFinite(q.difficulty as any) ? (q.difficulty as number) : 0;
      if (!byCat.has(cat)) byCat.set(cat, []);
      byCat.get(cat)!.push(q);
    }

    // 2) category order (ascending numeric for deterministic cycle)
    const cats = Array.from(byCat.keys()).sort((a, b) => a - b);

    // 3) shuffle questions inside each category, and also shuffle answers of each question
    for (const c of cats) {
      const shuffledQs = shuffle(byCat.get(c)!).map((q) => ({
        ...q,
        answers: shuffle(q.answers || []),
      }));
      byCat.set(c, shuffledQs);
    }

    // 4) interleave: cycle over categories, take next from each until count or buckets exhausted
    const pointers = new Map<number, number>(cats.map((c) => [c, 0]));
    const result: Q[] = [];
    let catIdx = 0;
    while (result.length < count && cats.length > 0) {
      const c = cats[catIdx % cats.length];
      const ptr = pointers.get(c)!;
      const bucket = byCat.get(c)!;
      if (ptr < bucket.length) {
        result.push(bucket[ptr]);
        pointers.set(c, ptr + 1);
      } else {
        // remove exhausted category
        cats.splice(catIdx % cats.length, 1);
        // don't advance catIdx on removal
        continue;
      }
      catIdx++;
      if (cats.length === 0) break;
    }
    return result;
  }, [questions, quiz?.questionAmount, quiz?.hasCategory]);

  const currentQ = preparedQuestions[qIndex];

  // start timer only in time-mode, game step, active campaign, and (if before-form) data collected
  useEffect(() => {
    if (step !== "game") return;
    if (!isTimeMode || isActive === false) return;
    if (collectUserData === 2 && !dataCollected) return;

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    timerRef.current = window.setInterval(() => setElapsed((p) => p + 1), 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [step, isTimeMode, isActive, collectUserData, dataCollected]);

  useEffect(() => {
    if (isActive === false) {
      Swal.fire({
        title: "Oyun Bitti",
        text: "Oyun sona erdi. Katıldığınız için teşekkürler.",
        icon: "warning",
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        showCloseButton: false,
        showConfirmButton: false,
      });
    }
  }, [isActive]);

  useEffect(() => {
    if (collectUserData === 2) setOpenModal(true);
  }, [collectUserData]);

  // answer click with continueOnWrong support
  const onAnswerClick = (aIndex: number) => {
    if (!currentQ || !currentQ.answers || locked || step !== "game") return;

    // every click counts as a move in moves-mode
    setMoves((m) => m + 1);
    setAnswerJustClicked(aIndex);

    const ans = currentQ.answers[aIndex];
    const isCorrect = !!ans?.isCorrect;

    // When continueOnWrong === false: first wrong ends the game (points = correctCount)
    if (quiz?.continueOnWrong === false && !isCorrect) {
      setLocked(true);
      // brief red flash
      setTimeout(() => {
        finalizeQuiz({ terminatedEarly: true });
      }, 600);
      return;
    }

    if (isCorrect) {
      setLocked(true); // brief lock to show green
      setCorrectCount((c) => c + 1);
      setTimeout(() => {
        setLocked(false);
        setAnswerJustClicked(null);
        if (qIndex >= preparedQuestions.length - 1) {
          finalizeQuiz({ terminatedEarly: false });
        } else {
          setQIndex((i) => i + 1);
        }
      }, 600);
    } else {
      // continueOnWrong === true ⇒ lock 2s then allow retry on same question
      setLocked(true);
      setTimeout(() => {
        setLocked(false);
        setAnswerJustClicked(null);
      }, 2000);
    }
  };

  const buildPlayerData = (
    formValues: any,
    formState: FormStateProps["formState"],
    campaignId: string,
    playerId: string
  ) => ({
    id: playerId,
    campaignId,
    firstName: formValues.firstName || "",
    lastName: formValues.lastName || "",
    gender: formState.formGender ? (formValues.gender === 1 ? 1 : 2) : 0,
    email: formState.formEmail ? formValues.email || "" : "",
    phone: formState.formPhone ? formValues.phone || "" : "",
    company: formState.formCompany ? formValues.company || "" : "",
    formAddPermissionChecked: formState.formAddPermission ? formValues.formAddPermissionChecked || false : false,
    formAddPermission2Checked: formState.formAddPermission2 ? formValues.formAddPermission2Checked || false : false,
    bonusField: formState.bonusField ? formValues[formState.bonusField] || "" : "",
    bonusFieldLabel: collectUserData != 1 ? formState.bonusField || "" : "",
  });

  const handleSubmit = async (values: UserDataValues) => {
    const playerId = player?.id || uuid();
    const playerData = buildPlayerData(values, formState, campaignId, playerId);

    if (collectUserData === 2) {
      // BEFORE
      setPlayer(playerData);
      setDataCollected(true); // timer starts if time mode
      setOpenModal(false);
      return true;
    }

    if (collectUserData === 3) {
      // AFTER — update player record then show scoreboard (if game already created)
      await agent.Players.update(playerData);
      setOpenModal(false);
      if (currentGameId) await goScoreboard(currentGameId);
      return true;
    }

    // NONE — optional, sync anyway
    await agent.Players.update(playerData);
    return true;
  };

  const handleClose = () => {
    if (collectUserData !== 2 && !dataCollected) setPlayer(null);
    setOpenModal(false);
  };

  const finalizeQuiz = async ({ terminatedEarly }: { terminatedEarly: boolean }) => {
    // stop timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const newGameId = uuid();

    // scoring mode
    const usingCountPoints = quiz?.continueOnWrong === false;
    const timeCompleted = usingCountPoints ? undefined : (isTimeMode ? elapsed : moves);
    const points = usingCountPoints ? correctCount : (isTimeMode ? elapsed : moves);

    // player
    let effectivePlayer: Player | null = null;
    if (collectUserData === 2 && player) {
      effectivePlayer = player;
    } else if (collectUserData === 3) {
      const pid = player?.id || uuid();
      effectivePlayer = {
        id: pid,
        campaignId,
        firstName: "",
        lastName: "",
        gender: 0,
        email: "",
        phone: "",
        company: "",
        formAddPermissionChecked: false,
        formAddPermission2Checked: false,
        bonusField: "",
        bonusFieldLabel: "",
      };
    } else {
      const pid = uuid();
      effectivePlayer = {
        id: pid,
        campaignId,
        firstName: "",
        lastName: "",
        gender: 0,
        email: "",
        phone: "",
        company: "",
        formAddPermissionChecked: false,
        formAddPermission2Checked: false,
        bonusField: "",
        bonusFieldLabel: "",
      };
    }

    // prize text
    const msg = usingCountPoints
      ? `Doğru Sayısı: ${correctCount}`
      : isTimeMode
      ? `Tamamladığınız Süre: ${formatMMSS(elapsed)}`
      : `Tamamladığınız Hamle: ${moves}`;

    // hasWon: only true if all questions finished correctly when using count points;
    // otherwise always true (no losing scenario) like before.
    const finishedAll = correctCount === preparedQuestions.length && !terminatedEarly;
    const hasWon = usingCountPoints ? finishedAll : true;

    const payload: any = {
      id: newGameId,
      gameType: 6, // Quiz
      prize: usingCountPoints
        ? `${correctCount} doğru`
        : isTimeMode
        ? `${elapsed} saniye`
        : `${moves} hamle`,
      isPrize: true,
      hasWon,
      datePlayed: new Date().toISOString(),
      campaignId,
      points,
      playerId: effectivePlayer.id,
      player: effectivePlayer,
    };

    if (!usingCountPoints) {
      // keep timeCompleted only for time/moves modes
      payload.timeCompleted = timeCompleted;
    }

    await Swal.fire({
      title: usingCountPoints ? "Oyun Bitti" : "Quiz Bitti",
      text: msg,
      icon: "success",
      showConfirmButton: false,
      allowEscapeKey: false,
      didOpen: () => {
        document.addEventListener("click", () => Swal.close(), { once: true });
      },
    });

    try {
      await agent.Games.create(payload);
      setPlayer(effectivePlayer);
      setCurrentGameId(newGameId);

      if (collectUserData === 3) {
        setOpenModal(true); // after-form
        return;
      }
    } catch (e) {
      console.error("Failed to create Quiz game", e);
    }

    await goScoreboard(newGameId, usingCountPoints);
  };

  const goScoreboard = async (gameId?: string, usingCountPoints?: boolean) => {
    const id = gameId ?? currentGameId;
    if (id) {
      try {
        // reversed = false when using count points (higher is better),
        // reversed = true for time/moves (lower is better)
        const reversed = usingCountPoints ? false : true;
        const sb = await agent.Games.scoreboard(campaignId, id, 10, reversed);
        setScoreboard(sb);
      } catch (e) {
        console.error("Failed to load scoreboard", e);
      }
    }
    setStep("scoreboard");
  };

  if (!preparedQuestions.length) {
    return (
      <div id="quiz" className="step-box">
        <h1>Quiz</h1>
        <h3>Soru bulunamadı.</h3>
      </div>
    );
  }

  const topIndicator = (() => {
    if (step !== "game") return null;
    if (quiz?.continueOnWrong === false) {
      return <h3 style={{color: textColor, textAlign: "center"}}>Doğru: {correctCount}</h3>;
    }
    if (isTimeMode) {
      return <h3 style={{color: textColor, textAlign: "center"}}>Süre: {formatMMSS(elapsed)}</h3>;
    }
    return <h3 style={{color: textColor, textAlign: "center"}}>Hamle: {moves}</h3>;
  })();

  // scoreboard unit label
  const unitLabel =
    quiz?.continueOnWrong === false ? " doğru" : isTimeMode ? " sn" : " hamle";

  const resetGame = () => {
    setMoves(0);
    setElapsed(0);
    setQIndex(0);
    setCorrectCount(0);
    setAnswerJustClicked(null);
    setLocked(false);
    setCurrentGameId(null);
    setScoreboard(null);

    if (collectUserData === 2) {
      setPlayer(null);
      setDataCollected(false);
      setOpenModal(true);
    }

    setStep("game");
  };

  return (
    <>
      <div id="quiz">
        {step === "game" && currentQ && (
          <div className="step-box inner quiz">
            {topIndicator}
            <div className="quiz-box">
              <p className="question" style={{color: textColor}}>{currentQ.text}</p>
              <ul className="answers">
                {(currentQ.answers || []).map((a, i) => {
                  const isSelected = i === answerJustClicked;
                  const feedbackClass =
                    isSelected && locked ? (a.isCorrect ? "correct" : "wrong") : "";
                  return (
                    <li
                      key={a.id || i}
                      className={feedbackClass}
                      onClick={() => onAnswerClick(i)}
                      style={{ pointerEvents: locked ? "none" : "auto", color: textColor, borderColor: textColor }}
                    >
                      {a.text}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}

        {step === "scoreboard" && (
          <div className="step-box scoreboard matchwin" onClick={resetGame}>
            <h4 className="score-title" style={{color: textColor}}>Sıralama</h4>
            <div className="scoreboard-container no-scrollbar">
              {scoreboard?.entries?.length
                ? scoreboard.entries.map((row) => (
                    <div
                      key={row.rank}
                      style={row.isActive ? {} : {color: textColor}}
                      className={`score-row ${row.isActive ? "active" : ""}`}
                    >
                      <span className="rank">{row.rank}.</span>
                      <span className="name">{row.name || "—"}</span>
                      <span className="pts">
                        {row.points}
                        {unitLabel}
                      </span>
                    </div>
                  ))
                : [...Array(10)].map((_, i) => (
                    <div key={i} className="score-row">
                      <span className="rank">{i + 1}.</span>
                      <span className="name">———</span>
                      <span className="pts">0{unitLabel.trim() ? unitLabel : ""}</span>
                    </div>
                  ))}
            </div>            
          </div>
        )}

        <UserDataModal
          open={collectUserData === 2 && !dataCollected ? true : openModal}
          onClose={handleClose}
          formState={formState}
          initialValues={initialFormValues}
          onSubmit={handleSubmit}
          submitLabel="Gönder"
        />
      </div>
    </>
  )
}

export default Quiz;